import React, { useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getRovoTip, setRovoTip } from '@atlassian/search-common/quick-find-rovo-tip';
import { EnlargedReturnIconContainer } from '@atlassian/search-dialog/search-result.styled';

import { onBannerDismissed } from '../../../../../common/utils/events/banner-dismissed';
import { useQuickFindAnalytics } from '../../../../../controllers/quick-find/analytics';
import { usePrimaryProduct, useUser } from '../../../../../controllers/store/bootstrap';

import { messages } from './messages';

const RovoTipMessageStyle = xcss({
	width: '95%',
	marginLeft: 'space.050',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	paddingLeft: 'space.050',
});

const RovoTipContainerStyle = xcss({
	paddingBottom: 'space.075',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
});

const RovoTipStyle = xcss({
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	position: 'relative',
	boxSizing: 'border-box',
	minHeight: token('space.500'),
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.accent.purple.subtlest',
	color: 'color.text.accent.purple',
});

export const RovoTipBanner = (): React.JSX.Element | null => {
	const [showRovoTip, setShowRovoTip] = useState<boolean>(false);
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const [primaryProduct] = usePrimaryProduct();
	const [user] = useUser();

	useEffect(() => {
		if (!primaryProduct || !user || !user.id) {
			return;
		}

		const rovoTipFromLocalStorage = getRovoTip(primaryProduct, user.id);
		setShowRovoTip(rovoTipFromLocalStorage);
	}, [primaryProduct, user]);

	const dismissRovoTip = useCallback(() => {
		if (!primaryProduct || !user || !user.id) {
			return;
		}

		const rovoTipFromLocalStorage = setRovoTip(primaryProduct, user.id);
		setShowRovoTip(rovoTipFromLocalStorage);
	}, [primaryProduct, user]);

	if (!showRovoTip) {
		return null;
	}

	return (
		<Box xcss={RovoTipContainerStyle}>
			<Box xcss={RovoTipStyle}>
				<LightbulbIcon
					spacing="spacious"
					label="blank"
					color={token('color.text.accent.purple')}
				/>
				<Box xcss={RovoTipMessageStyle}>
					<b>
						<FormattedMessage {...messages.rovoTip} />
					</b>{' '}
					<FormattedMessage {...messages.rovoBannerMessage} />
				</Box>
				<EnlargedReturnIconContainer>
					<IconButton
						icon={CrossIcon}
						spacing={'compact'}
						onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
							event.preventDefault();
							event.stopPropagation();
							dismissRovoTip();
							fireAnalyticsEvent(
								onBannerDismissed({
									isOnSearchDialog: true,
									componentName: 'RovoTipBanner',
								}),
							);
						}}
						label={<FormattedMessage {...messages.dismissBannerLabel} />}
						appearance={'subtle'}
					/>
				</EnlargedReturnIconContainer>
			</Box>
		</Box>
	);
};
